import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";

import { type TimeFrameFilter } from "@/components/OrderSearchBar";
import {
  OrderApi,
  type OrderDuplicateOrderCommandDuplicateFilesOptionEnum,
  type OrderGetOrderDetailsReadDto,
  OrdersAccountControllerGetAccountOrdersFilterEnum,
  OrdersControllerGetOrdersFilterEnum,
  type OrderValidateCommand,
} from "@/config/apiClient";
import { type ApiErrorResponse } from "@/config/apiClient.types";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";
import { useSuggestedFixRedirect } from "../useSuggestedFixRedirect";

interface QueryGetOrders {
  currentPage: number;
  filter?: {
    status?: OrdersControllerGetOrdersFilterEnum;
  };
  timeFrameFilter?: TimeFrameFilter;
  search?: string;
  orderBy?: "createdAt" | "status";
  nbPerPage?: number;
  asRoleClient?: boolean;
  assignedOnly?: boolean;
}

interface QueryGetAccountOrders {
  currentPage: number;
  filter?: {
    status?: OrdersAccountControllerGetAccountOrdersFilterEnum;
  };
  timeFrameFilter?: TimeFrameFilter;
  search?: string;
  orderBy?: "createdAt" | "status";
  nbPerPage?: number;
  assignedOnly?: boolean;
  enabled?: boolean;
}

type QueryGetAccountOrdersParams = {
  page: string;
  filter?: string;
  search?: string;
  startDate?: string;
  stopDate?: string;
  orderBy?: "createdAt" | "status";
  nbPerPage?: string;
};

const defaultTimeFrameFilter: TimeFrameFilter = { title: "default" };

const apiClient = createApiClient(OrderApi);

export const useOrders = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useCreateOrder = () => {
    return useMutation({
      mutationKey: ["createOrder"],
      mutationFn: ({
        orderName,
        assignedClientsIds,
      }: {
        orderName: string;
        assignedClientsIds?: string[];
      }) =>
        apiClient.ordersControllerCreateOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderCreateCommand: {
            orderName: orderName,
            assignedClientsIds: assignedClientsIds,
          },
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });
  };

  const useValidateOrder = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.ordersControllerValidateOrder>>,
      Error,
      { orderId: string; orderValidateCommand: OrderValidateCommand }
    >({
      mutationKey: ["validateOrder"],
      mutationFn: (params) =>
        apiClient.ordersControllerValidateOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderValidateCommand: params.orderValidateCommand,
          orderId: params.orderId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useGetOrders = ({
    currentPage = 0,
    filter = {
      status: OrdersControllerGetOrdersFilterEnum.All,
    },
    timeFrameFilter = defaultTimeFrameFilter,
    search = "",
    orderBy = "createdAt",
    nbPerPage = 10,
    asRoleClient = false,
    assignedOnly = true,
  }: QueryGetOrders) =>
    useQuery({
      queryKey: [
        "orders",
        orderBy,
        timeFrameFilter,
        currentPage,
        filter,
        search,
        nbPerPage,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
        asRoleClient,
        assignedOnly,
      ],
      enabled: !!currentWorkspace,
      queryFn: () => {
        const queryParams: {
          page: string;
          filter?: string;
          search?: string;
          startDate?: string;
          stopDate?: string;
          orderBy?: "createdAt" | "status";
          nbPerPage?: string;
        } = {
          page: String(currentPage),
          filter: JSON.stringify({
            status: filter.status ?? OrdersControllerGetOrdersFilterEnum.All,
          }),
          orderBy,
          search: search || "",
          nbPerPage: String(nbPerPage),
        };

        queryParams.startDate =
          timeFrameFilter?.startDate !== undefined
            ? dayjs(timeFrameFilter?.startDate).toISOString()
            : undefined;
        queryParams.stopDate =
          timeFrameFilter?.stopDate !== undefined
            ? dayjs(timeFrameFilter?.stopDate).toISOString()
            : undefined;

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(([, v]) => v != null && v !== "")
        );

        return apiClient.ordersControllerGetOrders({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          ...cleanQueryParams,
          ...(asRoleClient ? { asRoleClient } : {}),
          ...(assignedOnly ? { assignedOnly } : {}),
        });
      },
    });

  const useGetAccountOrders = ({
    currentPage = 0,
    filter = {
      status: OrdersControllerGetOrdersFilterEnum.All,
    },
    timeFrameFilter = defaultTimeFrameFilter,
    search = "",
    orderBy = "createdAt",
    nbPerPage = 10,
    assignedOnly = true,
    enabled = true,
  }: QueryGetAccountOrders) =>
    useQuery({
      queryKey: [
        "account-orders",
        orderBy,
        timeFrameFilter,
        currentPage,
        filter,
        search,
        nbPerPage,
        currentWorkspace?.accountId,
        assignedOnly,
      ],
      enabled: !!currentWorkspace?.accountId && enabled,
      queryFn: () => {
        const queryParams: QueryGetAccountOrdersParams = {
          page: String(currentPage),
          filter: JSON.stringify({
            status:
              filter.status ??
              OrdersAccountControllerGetAccountOrdersFilterEnum.All,
          }),
          orderBy,
          search: search || "",
          nbPerPage: String(nbPerPage),
        };

        queryParams.startDate =
          timeFrameFilter?.startDate !== undefined
            ? dayjs(timeFrameFilter?.startDate).toISOString()
            : undefined;
        queryParams.stopDate =
          timeFrameFilter?.stopDate !== undefined
            ? dayjs(timeFrameFilter?.stopDate).toISOString()
            : undefined;

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) => value !== null && value !== ""
          )
        );

        return apiClient.ordersAccountControllerGetAccountOrders({
          accountId: currentWorkspace?.accountId,
          ...cleanQueryParams,
          assignedOnly,
        });
      },
    });

  const useAddPurchaseOrder = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.ordersControllerAddPurchaseOrder>>,
      Error,
      {
        orderId: string;
        orderAddPurchaseOrderCommand: Parameters<
          typeof apiClient.ordersControllerAddPurchaseOrder
        >[0]["orderAddPurchaseOrderCommand"];
      }
    >({
      mutationFn: (body) =>
        apiClient.ordersControllerAddPurchaseOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: body.orderId,
          orderAddPurchaseOrderCommand: body.orderAddPurchaseOrderCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          queryClient.refetchQueries({
            queryKey: ["documents", params.orderId],
          }),
        ]),
    });

  const useEditPurchaseOrder = () =>
    useMutation<
      Awaited<
        ReturnType<typeof apiClient.ordersControllerReplacePurchaseOrder>
      >,
      Error,
      {
        orderId: string;
        orderReplacePurchaseOrderCommand: Parameters<
          typeof apiClient.ordersControllerReplacePurchaseOrder
        >[0]["orderReplacePurchaseOrderCommand"];
      }
    >({
      mutationFn: (body) =>
        apiClient.ordersControllerReplacePurchaseOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: body.orderId,
          orderReplacePurchaseOrderCommand:
            body.orderReplacePurchaseOrderCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          queryClient.refetchQueries({
            queryKey: ["documents", params.orderId],
          }),
        ]),
    });

  const useSyncWithAws = () => {
    return useMutation({
      mutationFn: (params: { orderId: string }) =>
        apiClient.ordersControllerAwsSync({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
        }),
      meta: { suppressError: true },
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
        ]),
    });
  };

  const useGetOrder = ({ orderId }: { orderId?: string }) => {
    const query = useQuery<
      AxiosResponse<OrderGetOrderDetailsReadDto>,
      ApiErrorResponse
    >({
      enabled:
        !!currentWorkspace?.accountId &&
        !!currentWorkspace?.organizationId &&
        !!currentWorkspace?.id &&
        !!orderId,
      queryKey: [
        "order",
        orderId,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      queryFn: () =>
        apiClient.ordersControllerGetOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: orderId as string,
        }),
    });
    const { is404Error } = useSuggestedFixRedirect(query);

    return { ...query, is404Error };
  };

  const useGetOrderDocuments = ({ orderId }: { orderId?: string }) =>
    useMutation({
      mutationFn: () =>
        apiClient.ordersControllerDownloadAllOrderDocuments({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: orderId as string,
        }),
    });

  const useAssignUsers = () => {
    return useMutation({
      mutationKey: ["assignuser"],
      mutationFn: ({
        orderId,
        usersIds,
      }: {
        orderId: string;
        usersIds: string[];
      }) =>
        apiClient.ordersControllerAssignUsersToOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId,
          orderUsersAssignCommand: { usersIds },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId],
          }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId],
          }),
          queryClient.invalidateQueries({ queryKey: ["getUsers"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });
  };

  const useGetActiveOrders = () =>
    useQuery({
      enabled: !!currentWorkspace,
      queryKey: [
        "activeOrders",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      queryFn: () => {
        return apiClient.ordersControllerGetOrders({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          filter: "active",
          page: "0",
          nbPerPage: 999,
        });
      },
    });

  const useDeleteOrder = (orderId: string) =>
    useMutation({
      mutationKey: ["deleteOrder"],
      mutationFn: () =>
        apiClient.ordersControllerDeleteOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId,
        }),

      onSuccess: async () => {
        void queryClient.removeQueries({ queryKey: ["order", orderId] });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]);
      },
    });

  const useDuplicateOrder = () =>
    useMutation({
      mutationKey: ["duplicateOrder"],
      mutationFn: ({
        orderId,
        orderName,
        duplicateFilesOption,
      }: {
        orderId: string;
        orderName: string;
        duplicateFilesOption?: OrderDuplicateOrderCommandDuplicateFilesOptionEnum;
      }) =>
        apiClient.ordersControllerDuplicateOrder({
          orderId: orderId,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderDuplicateOrderCommand: {
            orderName: orderName,
            duplicateFilesOption: duplicateFilesOption,
          },
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useRushOrder = () => {
    return useMutation({
      mutationFn: ({
        orderId,
        orderItemId,
        newDeliveryDate,
        isValidatedByClient,
      }: {
        newDeliveryDate?: string;
        isValidatedByClient?: boolean;
        orderId: string;
        orderItemId: string;
      }) =>
        apiClient.ordersControllerHandleRushOrder({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: orderId,
          orderItemId: orderItemId,
          orderRushOrderCommand: {
            newDeliveryDate,
            ...(isValidatedByClient && {
              isValidatedByClient: isValidatedByClient,
            }),
          },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
        ]),
    });
  };

  const useOrderNameAvailability = () =>
    useMutation({
      mutationFn: (params: {
        orderName: string;
        orderIdToExclude?: string;
      }) => {
        const queryParams: {
          orderIdToExclude?: string;
        } = {
          orderIdToExclude: params.orderIdToExclude,
        };

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(([, v]) => v != null && v !== "")
        );

        return apiClient.ordersControllerCheckOrderNameAvailability({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderName: params.orderName,
          ...cleanQueryParams,
        });
      },
    });

  const useUpdateOrderName = () =>
    useMutation({
      mutationKey: ["updateOrderName"],
      mutationFn: ({
        orderId,
        orderName,
      }: {
        orderId: string;
        orderName: string;
      }) => {
        return apiClient.ordersControllerUpdateOrderName({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId,
          orderUpdateNameCommand: {
            name: orderName,
          },
        });
      },
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  return {
    useValidateOrder,
    useGetOrders,
    useGetAccountOrders,
    useGetActiveOrders,
    useGetOrder,
    useGetOrderDocuments,
    useAddPurchaseOrder,
    useEditPurchaseOrder,
    useSyncWithAws,
    useAssignUsers,
    useDeleteOrder,
    useDuplicateOrder,
    useRushOrder,
    useCreateOrder,
    useOrderNameAvailability,
    useUpdateOrderName,
  };
};
